import { faPlus, faBadgeDollar } from '@fortawesome/pro-regular-svg-icons'
import { Button, Card, type CardProps, Box, Flex, Text } from '@mantine/core'
import { useMemo, useCallback, useEffect } from 'react'

import {
  useMutationUpdateBooking,
  useQueryGetAllBookingProducts,
  useQueryGetAllBookingProductsUnavailable,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct, type ParameterID } from '@flecto/types'
import { Icon, useNotifications } from '@flecto/ui'

import { useModalBookingProductsSelection } from '../../hooks/useModalBookingProductsSelection'
import { useModalBookingProductsUnavailable } from '../../hooks/useModalBookingProductsUnavailable'
import { TableBookingProductsSelection } from '../TableBookingProductsSelection/TableBookingProductsSelection'
import styles from './styles.module.css'

export type CardBookingProductsSelectionProps = Omit<CardProps, 'children'> & {
  bookingId?: ParameterID
}

export function CardBookingProductsSelection({
  bookingId,
  ...props
}: CardBookingProductsSelectionProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })
  const { showNotification } = useNotifications()
  const { openModal } = useModalBookingProductsSelection({ bookingId })

  const { data: booking } = useQueryGetBookingById({ bookingId })

  const {
    data: bookingProducts,
    isLoading: isLoadingBookingProducts,
    isFetching: isFetchingBookingProducts,
    isRefetching: isRefetchingBookingProducts,
  } = useQueryGetAllBookingProducts({ bookingId })

  const { mutateAsync: updateBooking, isLoading: isResetingBookingPrices } =
    useMutationUpdateBooking()

  const { data: unavailableItems } = useQueryGetAllBookingProductsUnavailable({
    bookingId,
  })

  const { openModal: openModalBookingProductsUnavailable } =
    useModalBookingProductsUnavailable({ bookingId })

  const hasAnyCustomPrice = useMemo(() => {
    return bookingProducts?.some((product) => {
      return product.pricing_tiers?.some(
        (tier) => tier.selected && tier.suggested === false
      )
    })
  }, [bookingProducts])

  const handleRecalculatePrices = useCallback(async () => {
    try {
      const bookingProductsToUpdate = bookingProducts?.map((product) => ({
        id: product.id,
        quantity: product.quantity,
        force_suggested_booking_cost: true,
      }))

      await updateBooking({
        bookingId,
        booking: { booking_items_attributes: bookingProductsToUpdate },
        options: { force_suggested_booking_cost: true },
      })

      showNotification({
        type: 'success',
        title: t('notification_recalculate_prices_success_title'),
        message: t('notification_recalculate_prices_success_description'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_recalculate_prices_error'),
      })
    }
  }, [bookingProducts])

  useEffect(() => {
    if (unavailableItems.length > 0 && booking?.status === 'negotiating') {
      openModalBookingProductsUnavailable()
    }
  }, [unavailableItems, booking?.status])

  return (
    <Card {...props} withBorder>
      <Flex align='center' justify='space-between'>
        <Box style={{ flex: 1 }}>
          <Text fw={500}>{t('label_products')}</Text>
          <Text color='dimmed' size='sm'>
            {t('card_items_selection_description')}
          </Text>
        </Box>
      </Flex>
      {Boolean(bookingProducts?.length) && (
        <TableBookingProductsSelection
          mt='md'
          bookingProducts={bookingProducts as CoreBookingProduct[]}
          isLoading={isLoadingBookingProducts}
          withBorder={false}
          withPagination={false}
        />
      )}
      <Flex align='center' justify='space-between' mt='md'>
        <Button
          variant='outline'
          onClick={openModal}
          disabled={
            !booking?.start_at ||
            !booking.end_at ||
            booking.status === 'canceled' ||
            booking.status === 'completed'
          }
          loading={isFetchingBookingProducts || isRefetchingBookingProducts}
          leftSection={<Icon icon={faPlus} />}
        >
          {t('label_select_products')}
        </Button>
        {Boolean(bookingProducts?.length) && (
          <Button
            size='sm'
            variant='white'
            color='blue'
            loading={isResetingBookingPrices}
            disabled={
              !hasAnyCustomPrice ||
              booking?.status === 'canceled' ||
              booking?.status === 'completed'
            }
            onClick={handleRecalculatePrices}
            leftSection={<Icon icon={faBadgeDollar} size='xs' />}
            className={styles.recalculatePrices}
          >
            {t('label_action_recalculate_prices')}
          </Button>
        )}
      </Flex>
    </Card>
  )
}
